import { Alert, Button } from '@arco-design/web-react';
import { useLayoutEffect } from 'react';
import type { FlowPageBridge, FormSessionToken } from '@jukun/platform-sdk/shell';
import { PortalFormSessionHost } from '../../../components/PortalFormSessionHost';
import { usePortalFormSessionManager, usePortalFormSessionSnapshot } from '../../../runtime/formSessionContext';
import type { PortalDraftDetail } from '../model/drafts';
import { hasLocalDraftSave } from '../runtime/draftFormSelection';

export type DraftFormRuntimeState = Readonly<{
  draftPublicId: string; token?: FormSessionToken;
} & ({ status: 'loading' | 'failed' } | { status: 'ready'; bridge: FlowPageBridge })>;

/** The Shell owns the session; this Host only exposes its current display position. */
export function DraftSourceFormHost(props: {
  detail: PortalDraftDetail; token?: FormSessionToken; sourceFailure?: unknown;
  onRuntimeChange: (runtime: DraftFormRuntimeState) => void; onRetry: () => void;
}) {
  const manager = usePortalFormSessionManager();
  const session = usePortalFormSessionSnapshot().activeSession;
  const draftPublicId = props.detail.summary.draftPublicId;
  const matches = !!props.token && session?.token.sessionId === props.token.sessionId
    && session.snapshot.context.contextType === 'DRAFT'
    && session.snapshot.context.draftContext.draftPublicId === draftPublicId
    && session.snapshot.context.businessObjectPublicId === props.detail.summary.businessObjectPublicId;
  const value = matches && session.status === 'READY' ? manager.getBridge(session.token) : undefined;
  const bridge = value?.contextType === 'DRAFT' ? value.bridge : undefined;
  const eligible = !props.sourceFailure && !!bridge && hasLocalDraftSave(props.detail);
  useLayoutEffect(() => {
    if (matches && props.token && session?.status !== 'FAILED') manager.setSaveEligibility(props.token, eligible);
  }, [manager, matches, props.token?.sessionId, eligible, session?.status]);
  const status = props.sourceFailure || matches && session?.status === 'FAILED' ? 'failed'
    : matches && session?.status === 'READY' && bridge ? 'ready' : 'loading';
  useLayoutEffect(() => {
    const identity = { draftPublicId, token: props.token };
    props.onRuntimeChange(status === 'ready' && bridge ? { ...identity, status, bridge } : { ...identity, status: status === 'ready' ? 'loading' : status });
  }, [props.onRuntimeChange, draftPublicId, props.token?.sessionId, status, bridge]);
  return <div className="portal-draft-source-host">
    {props.sourceFailure && props.token ? <Alert type="error" title="草稿表单暂不可用"
      content="当前操作已停用，请重新加载详情。"
      action={<Button type="primary" size="small" onClick={props.onRetry}>重新加载</Button>} /> : null}
    {props.sourceFailure && !props.token ? <Alert type="error" title="草稿表单暂不可用"
      content="草稿信息仍可查看，请重新加载来源表单。"
      action={<Button type="primary" size="small" onClick={props.onRetry}>重新加载</Button>} />
      : <PortalFormSessionHost token={props.token} onRetry={props.onRetry}
        className="portal-draft-source-host__container" />}
  </div>;
}
